import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiMinus, HiOutlineRefresh, HiPlus } from 'react-icons/hi';
import { generatePath } from 'react-router-dom';

import { PriceReadModel, ProductCategory, UnitScheme, useGetPricesQuery } from '@client/api';
import {
  Button,
  EmptyPlaceholder,
  isDefinedString,
  LinkButton,
  Modal,
  ModalContent,
  ModalFooter,
  Pagination,
  SearchField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadIconCell,
  TableHeadRow,
  TableIconCell,
  TableRow,
  Text,
  toPriceString,
  useSearching,
} from '@client/toolkit';

import { ProductsPaths } from '../../pods';

export type ProductPricesAddModalProps = {
  onSubmit: (items: PriceReadModel[]) => void;
  onClose: () => void;
  open?: boolean;
  selected?: PriceReadModel[];
  title?: string;
  unitSchemes?: UnitScheme[];
  excludeUnitSchemes?: UnitScheme[];
  productCategories?: ProductCategory[];
  excludeProductCategories?: ProductCategory[];
};

export const ProductPricesAddModal = ({
  open = true,
  onSubmit,
  onClose,
  selected,
  title,
  unitSchemes,
  excludeUnitSchemes,
  productCategories,
  excludeProductCategories,
}: ProductPricesAddModalProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { search, debouncedSearch, updateSearch, clearSearch, hasSearch } = useSearching();

  const pageSize = 250; // Fetch more to allow client-side filtering
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const { currentData, data, isFetching, refetch } = useGetPricesQuery(
    {
      search: debouncedSearch ?? undefined,
      'pagination.Limit': pageSize,
      'pagination.Offset': pageOffset,
    },
    { skip: open === false },
  );

  // in order to have visible data when closing (open != true) animation we use data prop - currentData will be empty because of skip: open === false
  const allItems = useMemo(
    () => currentData?.items ?? (!open ? data?.items : []) ?? [],
    [currentData?.items, data?.items, open],
  );

  // Filter by unit schemes and/or product categories if provided
  const filteredItems = useMemo(() => {
    let items = allItems;

    // Exclude certain unit schemes if specified
    if (excludeUnitSchemes && excludeUnitSchemes.length > 0) {
      items = items.filter(
        (item) => item.product?.unit?.scheme == null || !excludeUnitSchemes.includes(item.product.unit.scheme),
      );
    }

    // Include only certain unit schemes if specified
    if (unitSchemes && unitSchemes.length > 0) {
      items = items.filter(
        (item) => item.product?.unit?.scheme != null && unitSchemes.includes(item.product.unit.scheme),
      );
    }

    // Exclude certain product categories if specified
    if (excludeProductCategories && excludeProductCategories.length > 0) {
      items = items.filter(
        (item) => item.product?.category == null || !excludeProductCategories.includes(item.product.category),
      );
    }

    // Include only certain product categories if specified
    if (productCategories && productCategories.length > 0) {
      items = items.filter(
        (item) => item.product?.category != null && productCategories.includes(item.product.category),
      );
    }

    return items;
  }, [allItems, unitSchemes, excludeUnitSchemes, productCategories, excludeProductCategories]);

  // Client-side pagination
  const searchedItems = useMemo(() => {
    return filteredItems.slice(pageOffset, pageOffset + pageSize);
  }, [filteredItems, pageOffset, pageSize]);

  const totalCount = filteredItems.length;

  const [selectedItems, setSelectedItems] = useState<PriceReadModel[]>(selected ?? []);
  const [initialCounts, setInitialCounts] = useState<Record<string, number>>({});

  const handleAddItem = (item: PriceReadModel) => {
    setSelectedItems((prev) => [...prev, item]);
  };

  const handleRemoveItem = (item: PriceReadModel) => {
    const currentTotal = selectedItems.filter((x) => x.id === item.id).length;
    const baseline = initialCounts[item.id ?? ''] ?? 0;
    if (currentTotal <= baseline) return; // Don't remove below initial count
    setSelectedItems((prev) => {
      const idx = prev.findLastIndex((x) => x.id === item.id);
      if (idx === -1) return prev;
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  };

  const getNewCount = (item: PriceReadModel) => {
    const currentTotal = selectedItems.filter((x) => x.id === item.id).length;
    const baseline = initialCounts[item.id ?? ''] ?? 0;
    return Math.max(0, currentTotal - baseline);
  };

  const handleChangeSearch = (value: string | null) => {
    setPage(0);
    updateSearch(value);
  };

  const handleClickRefresh = () => {
    refetch();
  };

  // reset selected data when reopening or initial opening
  useEffect(() => {
    if (!open) return;
    const items = selected ?? [];
    setSelectedItems(items);
    // Compute baseline counts from already-existing products
    const counts: Record<string, number> = {};
    items.forEach((x) => {
      counts[x.id ?? ''] = (counts[x.id ?? ''] ?? 0) + 1;
    });
    setInitialCounts(counts);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      open={open}
      appear
      size="xl"
      onClose={onClose}
      onBackdropClick={onClose}
      title={tCommon('addEntity', { entity: title?.toLowerCase() })}
    >
      <ModalContent>
        <div className="flex flex-col h-[35rem] px-4">
          <div className="pb-4 flex items-center justify-between">
            <SearchField
              value={search}
              onChange={handleChangeSearch}
              onClear={clearSearch}
              placeholder={tCommon('search')}
              progressing={isFetching && hasSearch}
            />
            <div className="flex items-stretch space-x-2">
              <LinkButton
                size="sm"
                color="primary"
                to={generatePath(ProductsPaths.ProductCreate)}
                target="_blank"
                linkClassName="flex items-stretch"
              >
                {tCommon('addEntity', { entity: tGlobal('entity.product').toLowerCase() })}
              </LinkButton>
              <Button size="icon" color="primary-light" className="text-neutral-700" onClick={handleClickRefresh}>
                <HiOutlineRefresh />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto rounded-t-lg border-t border-l border-r border-border">
            <Table size="xs" layout="fixed" className="relative w-full">
              <TableHead className="sticky top-0 z-50">
                <TableHeadRow>
                  <TableHeadIconCell className="w-[80px]" />
                  <TableHeadCell className="w-[160px]">{tGlobal('product.name')}</TableHeadCell>
                  <TableHeadCell className="w-[90px]">{tGlobal('price.type')}</TableHeadCell>
                  <TableHeadCell className="w-[100px]">{tGlobal('product.priceMode')}</TableHeadCell>
                  <TableHeadCell className="w-auto">{tGlobal('price.unitAmount')}</TableHeadCell>
                </TableHeadRow>
              </TableHead>

              {searchedItems.length <= 0 && !isFetching ? (
                <caption className="px-4 py-4 [caption-side:bottom]">
                  <EmptyPlaceholder>
                    {tCommon('noEntityFound', { entity: tGlobal('entity.agreementProduct', { context: 'plural' }) })}
                  </EmptyPlaceholder>
                </caption>
              ) : (
                <TableBody>
                  {isFetching &&
                    Array.from(Array(pageSize).keys()).map((x) => (
                      <TableRow key={x}>
                        <TableIconCell>
                          <HiPlus className="ml-2 mr-2 h-5 w-5" style={{ color: '#e1a953' }} />
                        </TableIconCell>
                        <TableCell loading />
                        <TableCell loading />
                        <TableCell loading />
                        <TableCell loading />
                      </TableRow>
                    ))}
                  {!isFetching &&
                    searchedItems.map((searchedItem, idx) => {
                      const count = getNewCount(searchedItem);
                      return (
                        <TableRow
                          key={idx}
                          onClick={() => count === 0 ? handleAddItem(searchedItem) : undefined}
                          hover={count === 0}
                        >
                          <TableIconCell>
                            <div className="flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
                              {count > 0 ? (
                                <>
                                  <button
                                    type="button"
                                    className="p-0.5 rounded bg-[#322F2C] hover:bg-neutral-600 transition-colors"
                                    onClick={() => handleRemoveItem(searchedItem)}
                                  >
                                    <HiMinus className="h-4 w-4 text-white" />
                                  </button>
                                  <span className="text-xs font-semibold min-w-[16px] text-center" style={{ color: '#e1a953' }}>{count}</span>
                                  <button
                                    type="button"
                                    className="p-0.5 rounded bg-[#322F2C] hover:bg-neutral-600 transition-colors"
                                    onClick={() => handleAddItem(searchedItem)}
                                  >
                                    <HiPlus className="h-4 w-4" style={{ color: '#e1a953' }} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="p-0.5 rounded bg-[#322F2C] hover:bg-neutral-600 transition-colors"
                                  onClick={() => handleAddItem(searchedItem)}
                                >
                                  <HiPlus className="h-5 w-5" style={{ color: '#e1a953' }} />
                                </button>
                              )}
                            </div>
                          </TableIconCell>
                          <TableCell className="text-ellipsis overflow-hidden">
                            <Text
                              className="text-sm font-medium whitespace-nowrap"
                              isEmpty={!isDefinedString(searchedItem?.product?.name)}
                              title={searchedItem?.product?.name || undefined}
                            >
                              {searchedItem?.product?.name}
                            </Text>
                          </TableCell>
                          <TableCell className="text-ellipsis overflow-hidden">
                            <Text className="text-sm font-normal whitespace-nowrap">
                              {searchedItem?.recurring ? tGlobal('price.recurring') : tGlobal('price.oneTime')}
                            </Text>
                          </TableCell>
                          <TableCell className="text-ellipsis overflow-hidden">
                            <Text className="text-sm font-normal whitespace-nowrap">
                              {searchedItem?.product?.unit?.scheme 
                                ? `${tType(`UnitScheme.${searchedItem.product.unit.scheme}`)}${searchedItem.product.unit.label ? ` (${searchedItem.product.unit.label})` : ''}`
                                : tGlobal('product.standard')}
                            </Text>
                          </TableCell>
                          <TableCell className="text-ellipsis overflow-hidden">
                            <Text
                              className="text-sm font-normal whitespace-nowrap"
                              isEmpty={!isDefinedString(toPriceString(searchedItem))}
                              title={toPriceString(searchedItem) || undefined}
                            >
                              {toPriceString(searchedItem)}
                            </Text>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              )}
            </Table>
          </div>

          <div className="border border-border rounded-b-lg flex items-center">
            <Pagination
              className="flex-1"
              totalCount={totalCount}
              currentPage={page}
              setPage={setPage}
              pageSize={pageSize}
              visible
            />
          </div>
        </div>
      </ModalContent>

      <ModalFooter>
        <Button color="primary-dark" onClick={onClose}>
          {tCommon('cancel')}
        </Button>
        <Button onClick={() => onSubmit(selectedItems)}>{tCommon('save')}</Button>
      </ModalFooter>
    </Modal>
  );
};
